import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AppNavItem } from '../../services/menu-mapping';
import { NavigationService } from '../../services/navigation.service';

/**
 * Breadcrumb dinámico del toolbar — deriva el trail "Grupo › Item" a partir
 * de la URL actual cruzada contra el menú del NavigationService.
 *
 * Por qué no por route data (`{ path: 'clientes', data: { breadcrumb: 'Clientes' } }`):
 *   - Tendríamos que hardcodear el label en cada Route — duplica lo que ya
 *     viene traducido del backend via `menu-mapping`.
 *   - Si el admin renombra un módulo en el backend o el i18n cambia, las
 *     rutas no se enteran. Cruzando contra `NavigationService.currentItems`,
 *     el breadcrumb sigue al menú automáticamente.
 *
 * Casos de uso:
 *   - `/clientes`            → "Comercial › Clientes" (si el menu lo tiene anidado)
 *   - `/clientes/123`        → "Comercial › Clientes" (subimos al parent)
 *   - `/home`                → null (no mostrar, el dashboard es el "default")
 *   - `/legacy/xxx`          → null (placeholder, no necesita breadcrumb)
 *   - `/auth/sign-in`        → null (auth no tiene shell)
 *   - ruta desconocida       → null
 *
 * Lo expone como signal `trail()` con los segmentos { label, path } — el
 * componente del header puede renderizarlo con clicks de navegación a cada
 * nivel intermedio. El último segmento es la pagina actual (sin link).
 */
export interface BreadcrumbSegment {
  label: string;
  /** path al que linkea — undefined en el último segmento (página actual). */
  path?: string;
  /** Icono del NavigationService para acompañar el label del segmento. */
  icon?: string;
}

const SKIP_PATHS: ReadonlyArray<string | RegExp> = [
  /^\/auth(\/|$)/,
  /^\/legacy(\/|$)/,
  /^\/design-preview/,
  '/home',
  '/user-profile',
  '/changelog',
];

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private router = inject(Router);
  private nav = inject(NavigationService);

  private _trail = signal<BreadcrumbSegment[]>([]);
  /** Lista ordenada del primer ancestro al item actual. Vacío si no aplica. */
  readonly trail = computed(() => this._trail());
  /** Label del item actual (último segmento) — útil para mostrar como page title. */
  readonly currentLabel = computed(() => {
    const t = this._trail();
    return t.length > 0 ? t[t.length - 1].label : null;
  });
  readonly currentIcon = computed(() => {
    const t = this._trail();
    return t.length > 0 ? t[t.length - 1].icon : null;
  });

  constructor() {
    // Recomputar trail en cada nav + cada vez que el menú cambia (login,
    // cambio de permisos, cambio de idioma — NavigationService.items$ emite).
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.recompute(e.urlAfterRedirects));

    this.nav.items$.subscribe(() => {
      // Re-ejecutar contra la URL actual cuando el menú cambia (ej. re-translate).
      const currentUrl = this.router.url;
      this.recompute(currentUrl);
    });
  }

  private recompute(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    if (this.shouldSkip(path)) {
      this._trail.set([]);
      return;
    }

    // Buscamos el item del menu que matchea el path actual. Si no encontramos
    // exacto, subimos jerarquías intentando el path padre (`/clientes/123` → `/clientes`).
    const items = this.nav.currentItems;
    const chain = this.findChainByPath(items, path) ?? this.findChainByParentPath(items, path);
    if (!chain || chain.length === 0) {
      this._trail.set([]);
      return;
    }

    const segments: BreadcrumbSegment[] = chain.map((item, idx) => ({
      label: item.text,
      icon: item.icon,
      // El último segmento no linkea (es la página actual).
      path: idx === chain.length - 1 ? undefined : item.path || undefined,
    }));
    this._trail.set(segments);
  }

  private shouldSkip(path: string): boolean {
    return SKIP_PATHS.some(p =>
      typeof p === 'string' ? p === path : p.test(path),
    );
  }

  /** Devuelve la cadena completa de items del menu desde la raíz hasta el item
   *  con path coincidente. null si no se encuentra. */
  private findChainByPath(items: AppNavItem[] | undefined, target: string, chain: AppNavItem[] = []): AppNavItem[] | null {
    if (!items) return null;
    for (const it of items) {
      const nextChain = [...chain, it];
      if (it.path === target) return nextChain;
      if (it.items?.length) {
        const nested = this.findChainByPath(it.items, target, nextChain);
        if (nested) return nested;
      }
    }
    return null;
  }

  /** Si no hay match exacto, intenta con paths padre (`/clientes/123` → `/clientes`).
   *  Útil para vistas de detalle que no son items propios del menu. */
  private findChainByParentPath(items: AppNavItem[] | undefined, target: string): AppNavItem[] | null {
    const parts = target.split('/').filter(Boolean);
    while (parts.length > 0) {
      parts.pop();
      const parentPath = '/' + parts.join('/');
      const chain = this.findChainByPath(items, parentPath);
      if (chain) return chain;
    }
    return null;
  }
}
